import {Injectable, OnDestroy} from '@angular/core';
import {Project, ProjectService} from '@mydocma/project';
import {BehaviorSubject, EMPTY} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';
import {MmProjectLoaderService} from './mm-project-loader.service';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {CustomerService} from '@mydocma/customer';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {PendingService} from '@mydocma/app-container';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {PersonalizationService} from '@mydocma/core';
import {Router} from '@angular/router';
import {MmRoutesEnum} from '../mm-routing/mm-routes.enum';
import {CurrentProjectStateService} from '@docma-mm-core/project/current-project-state.service';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {TranslateService} from '@ngx-translate/core';


@UntilDestroy()
@Injectable()
export class MmProjectService implements OnDestroy {


  private currentProjectSubject = new BehaviorSubject<MmProject>(null);
  private loadedSubject = new BehaviorSubject(false);
  public currentProject = this.currentProjectSubject.asObservable().pipe(
    switchMap(p => this.loadedSubject.pipe(filter(Boolean), map(l => p)))
  );
  private pendingState = new BehaviorSubject(false);
  public loadingState = this.pendingState.asObservable();

  constructor(
              private readonly customerService: CustomerService,
              private readonly personalizationService: PersonalizationService,
              private readonly projectService: ProjectService,
              private readonly pendingService: PendingService,
              private readonly projectLoaderService: MmProjectLoaderService,
              private readonly router: Router,
              private readonly currentProjectState: CurrentProjectStateService,
              private readonly notificationService: NotificationService,
              private readonly translateService: TranslateService) {

    this.pendingService.registerPendingState(this.pendingState);

    this.projectService.onCurrentProjectChanging.pipe(
      untilDestroyed(this),
      distinctUntilChanged(this.compareMydocmaProjects))
      .subscribe(s => this.pendingState.next(true));

    this.projectService.isLoadingInitial.pipe(
      filter(loading => !loading),
      switchMap(l => this.projectService.currentProject
        .pipe(
          distinctUntilChanged(this.compareMydocmaProjects),
          tap((p) => this.pendingState.next(true)),
          switchMap((p: Project) => this.projectLoaderService.loadProject(p).pipe(
            map(mmP => ({
              mydocmaProject: p,
              mmProject: mmP
            })),
            catchError(e => {
              console.error('MmProjectService: loadProject failed', e);
              this.pendingState.next(false);
              this.translateService.get('MM.project.loadError').subscribe(msg => this.notificationService.error(msg));
              return EMPTY;
            })
          )),
        )),
    ).subscribe(p => {
      this.loadedSubject.next(false);
      this.onProjectChanged(p);
      this.pendingState.next(false);
    });

    this.projectService.currentProject.subscribe(async (project) => {
      if (project) {
        this.currentProjectState.setCurrentProject({
          id: project.id,
          customerId: project.customerId
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.pendingService.removePendingState(this.pendingState);

  }

  private compareMydocmaProjects(p1, p2) {
    const id1 = p1 && p1.id ? p1.id : null;
    const id2 = p2 && p2.id ? p2.id : null;

    return id1 === id2;
  }

  private async onProjectChanged(pp: { mydocmaProject: Project, mmProject: MmProject }) {
    if (this.router.routerState.snapshot.url === `/${MmRoutesEnum.customers}`) {
      return;
    }

    if (pp && pp.mydocmaProject) {
      await this.personalizationService.setValue(PersonalizationKeysEnum.LastProject,
        new Project({id: pp.mydocmaProject.id, customerId: pp.mydocmaProject.customerId}));
    }

    this.notifyEndLoading(pp.mmProject);
  }

  private notifyEndLoading(mmProject: MmProject) {
    this.currentProjectSubject.next(mmProject);
    this.updateTranslationProperties(mmProject);
    this.loadedSubject.next(true);
  }

  public updateCurrentProject(project: MmProject) {
    this.currentProjectSubject.next(project);
    this.updateTranslationProperties(project);
  }

  private async updateTranslationProperties(project) {

  }
}
